import { useMemo } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import type { EffectKind, FxFrame } from '../../store/types';
import { defaultEffect, effectIntensity, effectKinds, withIntensity } from '../../effects';

interface Props { frame: FxFrame }

export function FxFrameView({ frame }: Props) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const frames = useCanvasStore((s) => s.doc.frames);
  const anchor = useMemo(
    () => (frame.anchorId ? frames.find((f) => f.id === frame.anchorId) : undefined),
    [frame.anchorId, frames],
  );

  if (!frame.anchorId) {
    return (
      <div className="p-2 text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
        No anchor. Drag onto a frame to control its effect.
      </div>
    );
  }
  if (!anchor) {
    return (
      <div className="p-2 text-xs" style={{ color: 'var(--color-warning)' }}>
        Anchor missing: <span className="font-mono">{frame.anchorId}</span>
      </div>
    );
  }

  const intensity = effectIntensity(anchor.effect);

  return (
    <div
      className="p-2 space-y-2 text-xs"
      onMouseDown={(e) => e.stopPropagation()}
      style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>
          effect
        </div>
        <select
          className="cf-select w-full rounded px-2 py-1"
          value={anchor.effect.kind}
          onChange={(e) =>
            dispatch({
              t: 'frame.setEffect',
              id: anchor.id,
              effect: defaultEffect(e.target.value as EffectKind),
            })
          }
          style={{
            background: 'var(--color-input-bg)',
            border: '1px solid var(--color-input-border)',
            color: 'var(--color-text)',
          }}
        >
          {effectKinds.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>
      <div>
        <div
          className="flex justify-between text-[10px] uppercase tracking-wider mb-1"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <span>intensity</span>
          <span>{(intensity * 100).toFixed(0)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={intensity}
          onChange={(e) =>
            dispatch({
              t: 'frame.setEffect',
              id: anchor.id,
              effect: withIntensity(anchor.effect, Number(e.target.value)),
            })
          }
          className="w-full"
          style={{ accentColor: 'var(--color-tool-fx)' }}
        />
      </div>
    </div>
  );
}
